// routes/PermissionRoute.js
import React from 'react';
import { useSelector } from 'react-redux';
import { hasPermission, isAdminUser } from 'utils/permissions';
import { Result, Button } from 'antd';

/**
 * PermissionRoute - Wrapper component that checks if user has required permission
 * 
 * @param {Object} props
 * @param {React.Component} props.component - Component to render if authorized
 * @param {string} props.resource - Resource name (e.g., 'events', 'users')
 * @param {string} props.action - Required action (e.g., 'read', 'create', 'update', 'delete')
 * @param {boolean} props.adminOnly - If true, only admin/super_admin can access
 */
const PermissionRoute = ({ component: Component, resource, action = 'read', adminOnly = false, ...rest }) => {
  const user = useSelector(state => state.auth.user);

  // Check adminOnly first
  if (adminOnly && !isAdminUser(user)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Result
          status="403"
          title="Access Denied"
          subTitle="This page is only accessible to administrators."
          extra={
            <Button type="primary" href="/app/my-dashboard">
              Go to My Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  // Check if user has the required permission (if resource is specified)
  const isAuthorized = resource ? hasPermission(user, resource, action) : true;

  if (!isAuthorized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Result
          status="403"
          title="Access Denied"
          subTitle="Sorry, you don't have permission to access this page."
          extra={
            <Button type="primary" href="/app/dashboard">
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  return <Component {...rest} />;
};

export default PermissionRoute;
